import { Ban, CheckCircle2, ClipboardList, History, Settings } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { ErrorState } from '../../../../components/ErrorState';
import { Menu } from '../../../../components/Menu';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Skeleton } from '../../../../components/Skeleton';
import { HistoryLogsModal } from '../../../../components/index-page/HistoryLogsModal';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { StatusText } from '../../../../components/table/StatusText';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime, toIso } from '../../../../lib/formatDate';
import { SurveyQuestionModal } from '../components/SurveyQuestionModal';
import { surveyQuestionsApi } from '../surveyApi';
import type { SurveyQuestion } from '../surveyTypes';

type TabKey = 'info';

/**
 * One survey question.
 *
 * On the **header** layout rather than the sidebar one: a catalog row reads as a single
 * continuous thing — what is asked, what the answer form draws for it, and the trail of who
 * changed the wording — not a record whose content splits into panels a reader chooses between.
 *
 * It earns its keep on the count: how many employees have already answered a question is what a
 * reader wants before rewording it, retiring it, or taking one of its follow-ups away.
 */
export function SurveyQuestionProfilePage() {
  const { id } = useParams<{ id: string }>();

  const [question, setQuestion] = useState<SurveyQuestion | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [editing, setEditing] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [tab, setTab] = useState<TabKey>('info');

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      setLoading(true);

      surveyQuestionsApi
        .get(id, signal)
        .then((next) => {
          if (!signal?.aborted) setQuestion(next);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;

          setError(
            caught instanceof ApiError ? caught : new ApiError('Could not load this question.', 0),
          );
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [id],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  // A plain string is a segment that goes nowhere; the two links are the way back.
  const breadcrumb: BreadcrumbSegment[] = [
    'Human Resources',
    'Personnel',
    { label: 'Settings', to: '/personnel/settings' },
    { label: 'Survey', to: '/personnel/settings/survey' },
    question ? `Item ${question.number}` : 'Question',
  ];

  if (error) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState
          message={error.message}
          correlationId={error.correlationId}
          onRetry={() => load()}
        />
      </div>
    );
  }

  if (loading || !question) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-3 p-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        id="survey-question-profile"
        header={
          <ProfileHeader
            id="survey-question-profile-header"
            icon={<ClipboardList className="h-6 w-6" aria-hidden="true" />}
            /*
              The item number is the title, not the question: the title is the record's
              identity, and a 600-character sentence as a page heading is unreadable. The
              question itself is the secondary line, where it has the width to be read.
            */
            title={`Item ${question.number}`}
            meta={[
              // Unlabelled, because "12 answers" says what it is on its own.
              `${question.recordCount} ${question.recordCount === 1 ? 'answer' : 'answers'}`,
            ]}
            secondary={[question.text]}
            status={
              <StatusText tone={question.isActive ? 'active' : 'inactive'}>
                {question.isActive ? 'Active' : 'Deleted'}
              </StatusText>
            }
            createdBy={question.createdByName}
            createdAt={question.createdAt}
            /*
              The record-scoped history. It lived on the index toolbar's cog until the
              questionnaire stopped being an index page; a profile puts it in its own header cog
              and scopes it with `entityId`, which is where CLAUDE.md says it belongs anyway.

              Its key is the audit module's, not this one's — a control carries the key of what
              it opens.
            */
            actions={
              <Menu
                id="survey-question-profile-settings"
                items={[
                  {
                    key: 'history',
                    label: 'History Logs',
                    icon: <History className="h-4 w-4" aria-hidden="true" />,
                    onClick: () => setHistoryOpen(true),
                    permission: 'audit-logs.read',
                  },
                ]}
                trigger={({ open, toggle }) => (
                  <button
                    type="button"
                    onClick={toggle}
                    aria-haspopup="menu"
                    aria-expanded={open}
                    aria-label="Question settings"
                    className="rounded-lg p-1 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
                  >
                    <Settings className="h-4 w-4" aria-hidden="true" />
                  </button>
                )}
              />
            }
          />
        }
        banner={
          question.isActive ? (
            <ProfileBanner
              id="survey-question-profile-banner"
              icon={<CheckCircle2 className="h-4 w-4" aria-hidden="true" />}
              message="This question is asked: it appears on the Survey panel of every personnel record."
            />
          ) : (
            <ProfileBanner
              id="survey-question-profile-banner"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message="This question is deleted. It is no longer asked, and the answers already given are kept."
            />
          )
        }
        tabs={[{ key: 'info', label: 'Info' }]}
        activeTab={tab}
        onTabChange={setTab}
      >
        <ProfileCard
          id="survey-question-details"
          title="Details"
          menuItems={[
            {
              key: 'edit',
              label: 'Edit',
              permission: 'survey-questions.update',
              onClick: () => setEditing(true),
            },
          ]}
        >
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Item number', value: question.number, mono: true },
              { label: 'Question', value: question.text },
              { label: 'Description', value: question.description },
              { label: 'Form guidance', value: question.guidance },
              {
                label: 'Order',
                value: <span className="font-mono tabular-nums">{question.sortOrder}</span>,
              },
              {
                label: 'Answers recorded',
                value: <span className="font-mono tabular-nums">{question.recordCount}</span>,
              },

              /*
                The five flags live on THIS card rather than a second one, and that is a rule
                rather than a layout preference: every card of facts owes a cog with an Edit in
                it, and one card is one modal — so a second card here would either have no Edit
                or a second Edit into the same form. The seam it would be split along is ours,
                not the reader's: they came to correct what the question asks.
              */
              {
                label: 'Details',
                value: question.collectsDetails
                  ? 'Asked, and required once the answer is Yes'
                  : 'Not asked',
              },
              {
                label: 'Date filed',
                value: question.collectsDate ? 'Asked, optional' : 'Not asked',
              },
              {
                label: 'Status of case',
                value: question.collectsCaseStatus ? 'Asked, optional' : 'Not asked',
              },
              {
                label: 'ID number',
                value: question.collectsIdNumber ? 'Asked, optional' : 'Not asked',
              },
              {
                label: 'Country',
                value: question.collectsCountry
                  ? 'Asked from the country catalog, and required once the answer is Yes'
                  : 'Not asked',
              },
            ]}
          />
        </ProfileCard>

        {/* Audit is never editable: created and updated are a record of what happened. */}
        <ProfileCard id="survey-question-audit" title="Audit">
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Created by', value: question.createdByName },
              {
                label: 'Created',
                value: (
                  <time dateTime={toIso(question.createdAt)} className="font-mono">
                    {formatDateTime(question.createdAt)}
                  </time>
                ),
              },
              { label: 'Updated by', value: question.updatedByName },
              {
                label: 'Updated',
                value: question.updatedAt ? (
                  <time dateTime={toIso(question.updatedAt)} className="font-mono">
                    {formatDateTime(question.updatedAt)}
                  </time>
                ) : null,
              },
            ]}
          />
        </ProfileCard>
      </ProfileHeaderShell>

      {editing ? (
        <SurveyQuestionModal
          open
          editing={question}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            load();
          }}
        />
      ) : null}

      <HistoryLogsModal
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        module="survey-questions"
        entityTypes={['SurveyQuestion']}
        entityId={question.id}
      />
    </div>
  );
}
